import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import ProgressStepper, { RESPONDENT_STEPS } from "../components/ProgressStepper";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { Mic, Square, CheckCircle2, ArrowRight } from "lucide-react";

export default function VoiceCapture() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [idx, setIdx] = useState(0);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState({});
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTsRef = useRef(0);
  const streamRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/voice/prompts")
      .then((r) => setPrompts(r.data))
      .catch(() => toast.error("Could not load voice prompts"));
  }, [user, loading, nav]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const durationSec = (Date.now() - startTsRef.current) / 1000;
        const prompt = prompts[idx];
        if (durationSec < (prompt?.min_seconds || 20)) {
          toast.error(`Please record at least ${prompt?.min_seconds || 20} seconds`);
          setElapsed(0);
          return;
        }
        await uploadSample(blob, prompt.id, durationSec);
      };
      mediaRef.current = rec;
      rec.start();
      startTsRef.current = Date.now();
      setElapsed(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTsRef.current) / 1000));
      }, 250);
    } catch (e) {
      toast.error("Microphone access denied. Please allow access to continue.");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  const uploadSample = async (blob, promptId, durationSec) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, `${promptId}.webm`);
      fd.append("prompt_id", promptId);
      fd.append("duration_seconds", String(durationSec));
      await api.post("/voice/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded((u) => ({ ...u, [promptId]: true }));
      toast.success("Voice sample uploaded");
    } catch (e) {
      // Voice-quality precheck returns 400 with a structured detail object:
      // {code:'voice_quality_failed', message, reasons:[...], checks:{...}}
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "voice_quality_failed") {
        toast.error(d.message || "Please re-record — audio quality was too low.",
                    { duration: 6000 });
        // Reset elapsed so the "please record ≥N seconds" reads correctly
        setElapsed(0);
      } else {
        toast.error(typeof d === "string" ? d : "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const currentPrompt = prompts[idx];
  const allDone = prompts.length > 0 && prompts.every((p) => uploaded[p.id]);

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-12">
        <ProgressStepper steps={RESPONDENT_STEPS} current="voice" />

        <div className="mb-8">
          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight">Neutral voice sample</h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Three short recordings — a natural spoken intro followed by two
            acoustic reference clips. Please speak in a quiet space.
          </p>
        </div>

        {!currentPrompt ? (
          <div className="text-muted-foreground">Loading prompts…</div>
        ) : (
          <Card className="mano-card">
            <CardContent className="p-6 md:p-10">
              <div className="flex items-center justify-between mb-6">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Prompt {idx + 1} of {prompts.length}
                </div>
                <div className="text-xs text-muted-foreground">Minimum {currentPrompt.min_seconds}s</div>
              </div>

              <p className="font-display text-xl sm:text-2xl leading-relaxed text-foreground" data-testid={`voice-prompt-${idx}`}>
                {currentPrompt.text}
              </p>

              <div className="mt-10 flex flex-col items-center">
                <button
                  type="button"
                  onClick={recording ? stop : uploaded[currentPrompt.id] ? null : start}
                  disabled={uploading || uploaded[currentPrompt.id]}
                  data-testid="voice-record-btn"
                  className={
                    "h-24 w-24 rounded-full flex items-center justify-center text-primary-foreground transition-colors duration-200 " +
                    (uploaded[currentPrompt.id]
                      ? "bg-secondary text-secondary-foreground"
                      : recording
                      ? "bg-destructive mano-pulse-ring"
                      : "bg-primary hover:bg-primary/90")
                  }
                >
                  {uploaded[currentPrompt.id] ? <CheckCircle2 size={36} /> : recording ? <Square size={30} /> : <Mic size={32} />}
                </button>
                <div className="mt-4 text-sm text-muted-foreground" aria-live="polite" data-testid="voice-elapsed">
                  {recording ? `${elapsed}s recording…`
                    : uploaded[currentPrompt.id] ? "Sample captured"
                    : uploading ? "Uploading…"
                    : "Tap to start"}
                </div>
              </div>

              <div className="mt-10 flex flex-wrap gap-3 justify-between">
                <Button
                  variant="outline"
                  disabled={idx === 0}
                  onClick={() => setIdx((i) => Math.max(0, i - 1))}
                  className="rounded-full px-6"
                  data-testid="voice-prev-btn"
                >
                  Previous prompt
                </Button>
                {idx < prompts.length - 1 ? (
                  <Button
                    disabled={!uploaded[currentPrompt.id]}
                    onClick={() => setIdx((i) => i + 1)}
                    className="rounded-full px-6 mano-lift"
                    data-testid="voice-next-btn"
                  >
                    Next prompt <ArrowRight size={16} />
                  </Button>
                ) : (
                  <Button
                    disabled={!allDone}
                    onClick={() => nav("/voice-intro")}
                    className="rounded-full px-6 mano-lift"
                    data-testid="voice-continue-btn"
                  >
                    Continue to questionnaires <ArrowRight size={16} />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
